import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { createSchool, fetchMySchools, updateSchool } from "../../lib/schools";
import { formatApiError } from "../../lib/api";
import { normalizeInstagramUrl } from "../../lib/instagram";
import { ReadableNavButton } from "../Sticker";
import { FieldHint } from "../createEvent/CreateEventFormUi";

/**
 * One school per host for now. Linked from Host → School when payments are enabled.
 */
export default function HostSchoolSetup({
  schoolName,
  schoolBio,
  schoolCity,
  schoolInstagram,
  whatsappUrl,
  enabled,
  onSchoolLoaded,
}) {
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setSchool(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchMySchools("ADMIN");
        if (cancelled) return;
        const next = data.managing?.[0] || null;
        setSchool(next);
        if (next) onSchoolLoaded?.(next);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [enabled]);

  async function handleSetup() {
    const name = (schoolName || "").trim();
    if (!name) {
      toast.error("Enter a school name first.");
      return;
    }
    setBusy(true);
    try {
      let target = school;
      const payload = {
        name,
        description: (schoolBio || "").trim() || null,
        city: (schoolCity || "").trim() || null,
        instagram: normalizeInstagramUrl(schoolInstagram) || null,
        whatsapp_url: (whatsappUrl || "").trim() || null,
      };
      if (target) {
        const patch = {};
        if (payload.name !== target.name) patch.name = payload.name;
        if (payload.description !== (target.description || null)) patch.description = payload.description;
        if (payload.city !== (target.city || null)) patch.city = payload.city;
        if (payload.instagram !== (target.instagram || null)) patch.instagram = payload.instagram;
        if (payload.whatsapp_url !== (target.whatsapp_url || null)) patch.whatsapp_url = payload.whatsapp_url;
        if (Object.keys(patch).length) {
          target = await updateSchool(target.id, patch);
          setSchool(target);
        }
      } else {
        target = await createSchool({
          name: payload.name,
          description: payload.description || undefined,
          city: payload.city || undefined,
          instagram: payload.instagram || undefined,
          whatsapp_url: payload.whatsapp_url || undefined,
        });
        setSchool(target);
        toast.success("School created");
      }
      navigate(`/admin/${target.slug}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (!enabled) return null;
  if (loading) {
    return (
      <div className="animate-pulse motion-reduce:animate-none space-y-2 pt-1" aria-busy="true" aria-label="Loading school">
        <div className="h-4 w-48 rounded-md2 bg-black/[0.06]" />
        <div className="h-11 w-full sm:w-56 rounded-md2 bg-black/[0.06]" />
        <p className="sr-only">Loading school…</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="host-school-setup">
      {school ? (
        <FieldHint>
          Linked: <span className="font-semibold text-ink">{school.name}</span>
          {" — "}open school management for classes, people, fees, and your invite link.
        </FieldHint>
      ) : (
        <FieldHint>
          Fill in the details below, then create your school to manage classes and invites.
        </FieldHint>
      )}
      <ReadableNavButton
        type="button"
        variant="primary"
        disabled={busy}
        onClick={handleSetup}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5"
        data-testid="setup-school-btn"
      >
        {busy ? "Opening…" : school ? "Setup Your School" : "Create school & continue"}
        {!busy && <ArrowRight size={14} aria-hidden />}
      </ReadableNavButton>
    </div>
  );
}
